// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pure part of the Chats panel. Every conversation, whatever app it
// lives in, is reduced to a ChatsPanelEntry; these helpers decide which
// section it sits in, whether a chip or a search filters it out, and how its
// time stamp reads. No stores, no React, so it can be unit tested directly.

export const CHAT_SOURCES = ['konvo', 'servers', 'whatsapp', 'teams', 'mail', 'clickup'] as const;
export type ChatsPanelSource = (typeof CHAT_SOURCES)[number];
/** One source, 'all', 'unread', or a combined selection saved as `'whatsapp,konvo'`. */
export type ChatsPanelFilter = 'all' | 'unread' | ChatsPanelSource | `${ChatsPanelSource},${string}`;

function isSource(value: string): value is ChatsPanelSource {
	return (CHAT_SOURCES as ReadonlyArray<string>).includes(value);
}

/** Sources in a comma-joined filter, de-duplicated and validated; empty for 'all'/'unread'. */
export function parseFilterSources(filter: string | undefined): Array<ChatsPanelSource> {
	if (!filter) return [];
	const sources: Array<ChatsPanelSource> = [];
	for (const part of filter.split(',')) {
		const source = part.trim();
		if (isSource(source) && !sources.includes(source)) sources.push(source);
	}
	return sources;
}

/**
 * Canonical persisted form of a saved value: 'all' or 'unread' as is, one or
 * more known sources comma-joined, anything unknown falls back to 'all'.
 */
export function normalizeFilter(value: string | undefined): ChatsPanelFilter {
	if (value === 'all' || value === 'unread') return value;
	const sources = parseFilterSources(value);
	if (sources.length === 0) return 'all';
	return sources.join(',') as ChatsPanelFilter;
}
export type ChatsPanelEntryKind = 'channel' | 'server_channel' | 'whatsapp' | 'mail' | 'clickup';

export interface ChatsPanelEntry<T = unknown> {
	/** Stable React key, unique across kinds. */
	key: string;
	kind: ChatsPanelEntryKind;
	source: ChatsPanelSource;
	/** Display name, used for the inline search. */
	name: string;
	/** Epoch ms of the newest activity, or null when nothing has happened yet. */
	lastAt: number | null;
	/** Whether the row should read as unread. Muted chats report false here. */
	hasUnread: boolean;
	/** Number to show in the pill; 0 falls back to a dot when hasUnread. */
	unreadCount: number;
	pinned: boolean;
	muted: boolean;
	data: T;
}

export interface ChatsPanelSections<T = unknown> {
	pinned: Array<ChatsPanelEntry<T>>;
	recent: Array<ChatsPanelEntry<T>>;
	quiet: Array<ChatsPanelEntry<T>>;
}

const DAY_MS = 86_400_000;

/**
 * A chat stays in Recent while it is alive: unread, or touched within the
 * quiet threshold. A threshold of 0 disables folding altogether.
 */
export function isAlive(entry: ChatsPanelEntry, nowMs: number, quietAfterDays: number): boolean {
	if (entry.hasUnread) return true;
	if (quietAfterDays <= 0) return true;
	if (entry.lastAt == null) return false;
	return nowMs - entry.lastAt < quietAfterDays * DAY_MS;
}

export function byLastActivityDesc(a: ChatsPanelEntry, b: ChatsPanelEntry): number {
	const at = a.lastAt ?? 0;
	const bt = b.lastAt ?? 0;
	if (bt !== at) return bt - at;
	return a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
}

/**
 * Split entries into Pinned, Recent and Quiet. Pinned keeps its incoming
 * order (the server owns pin order); the other two sort newest first.
 */
export function partitionEntries<T>(
	entries: ReadonlyArray<ChatsPanelEntry<T>>,
	options: {nowMs: number; quietAfterDays: number},
): ChatsPanelSections<T> {
	const pinned: Array<ChatsPanelEntry<T>> = [];
	const recent: Array<ChatsPanelEntry<T>> = [];
	const quiet: Array<ChatsPanelEntry<T>> = [];
	for (const entry of entries) {
		if (entry.pinned) pinned.push(entry);
		else if (isAlive(entry, options.nowMs, options.quietAfterDays)) recent.push(entry);
		else quiet.push(entry);
	}
	recent.sort(byLastActivityDesc);
	quiet.sort(byLastActivityDesc);
	return {pinned, recent, quiet};
}

/** `filter` is 'all', 'unread', one source, or several sources comma-joined ('whatsapp,konvo'). */
export function matchesFilter(entry: ChatsPanelEntry, filter: ChatsPanelFilter): boolean {
	switch (filter) {
		case 'all':
			return true;
		case 'unread':
			return entry.hasUnread;
		default: {
			if (entry.source === filter) return true;
			if (!filter.includes(',')) return false;
			return parseFilterSources(filter).includes(entry.source);
		}
	}
}

export interface SelectionLabels {
	/** Joins two items: "{a} and {b}". */
	pair: (a: string, b: string) => string;
	/** Joins the comma-separated head with the last item: "{head}, {last}" is
	 *  produced here; only the final "and" is localised, via `pair`. */
	separator: string;
}

/**
 * Human-readable name of a chip selection for section headers: "WhatsApp",
 * "WhatsApp and Konvo", "WhatsApp, Konvo and Mail". `names` maps each source
 * to its display name; `labels` supplies the localised joiners. Empty for
 * an empty selection.
 */
export function describeSelection(
	sources: ReadonlyArray<ChatsPanelSource>,
	names: Record<ChatsPanelSource, string>,
	labels: SelectionLabels = {pair: (a, b) => `${a} and ${b}`, separator: ', '},
): string {
	const items = sources.map((source) => names[source]).filter((name) => name.length > 0);
	if (items.length === 0) return '';
	if (items.length === 1) return items[0];
	const head = items.slice(0, -1).join(labels.separator);
	return labels.pair(head, items[items.length - 1]);
}

export function normalizeQuery(query: string): string {
	return query.trim().toLocaleLowerCase();
}

export function matchesQuery(name: string, normalizedQuery: string): boolean {
	if (!normalizedQuery) return true;
	return name.toLocaleLowerCase().includes(normalizedQuery);
}

export interface ChatsPanelCounts {
	unread: number;
	bySource: Record<ChatsPanelSource, number>;
}

/** Number of unread conversations, overall and per source (chats, not messages). */
export function countUnread(entries: ReadonlyArray<ChatsPanelEntry>): ChatsPanelCounts {
	const counts: ChatsPanelCounts = {
		unread: 0,
		bySource: {konvo: 0, servers: 0, teams: 0, whatsapp: 0, mail: 0, clickup: 0},
	};
	for (const entry of entries) {
		if (!entry.hasUnread) continue;
		counts.unread += 1;
		counts.bySource[entry.source] += 1;
	}
	return counts;
}

export interface ChatStampLabels {
	yesterday: string;
}

function sameDay(a: Date, b: Date): boolean {
	return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/**
 * Desktop Chats list: Recent splits into Today, Yesterday and Earlier by the
 * local calendar day of the last activity, matching the row stamps. Rows keep
 * their order inside each group; a row with no activity time is Earlier.
 */
export function splitByDay<T>(
	entries: ReadonlyArray<ChatsPanelEntry<T>>,
	nowMs: number,
): {today: Array<ChatsPanelEntry<T>>; yesterday: Array<ChatsPanelEntry<T>>; earlier: Array<ChatsPanelEntry<T>>} {
	const now = new Date(nowMs);
	// Calendar arithmetic, not minus 24h: a 23-hour DST day would land two days back.
	const yesterdayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
	const today: Array<ChatsPanelEntry<T>> = [];
	const yesterday: Array<ChatsPanelEntry<T>> = [];
	const earlier: Array<ChatsPanelEntry<T>> = [];
	for (const entry of entries) {
		const date = entry.lastAt != null && Number.isFinite(entry.lastAt) ? new Date(entry.lastAt) : null;
		if (date != null && sameDay(date, now)) today.push(entry);
		else if (date != null && sameDay(date, yesterdayDate)) yesterday.push(entry);
		else earlier.push(entry);
	}
	return {today, yesterday, earlier};
}

/**
 * Short right-aligned time for a row: clock today, "Yesterday", the weekday
 * within the last week, then day and month, with the year once it differs.
 */
export function formatChatStamp(
	ms: number | null | undefined,
	nowMs: number,
	labels: ChatStampLabels,
	locale?: string,
): string {
	if (ms == null || !Number.isFinite(ms)) return '';
	const date = new Date(ms);
	const now = new Date(nowMs);
	if (sameDay(date, now)) return stampFormat(locale, 'time').format(date);
	const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
	if (sameDay(date, yesterday)) return labels.yesterday;
	if (nowMs - ms < 6 * DAY_MS) return stampFormat(locale, 'weekday').format(date);
	if (date.getFullYear() === now.getFullYear()) return stampFormat(locale, 'day').format(date);
	return stampFormat(locale, 'year').format(date);
}

const STAMP_OPTIONS = {
	time: {hour: 'numeric', minute: '2-digit'},
	weekday: {weekday: 'short'},
	day: {day: 'numeric', month: 'short'},
	year: {day: 'numeric', month: 'short', year: 'numeric'},
} satisfies Record<string, Intl.DateTimeFormatOptions>;
const stampFormats = new Map<string, Intl.DateTimeFormat>();

/**
 * One formatter per locale and style: toLocaleString builds a new one on every call, which was
 * most of the time spent drawing the Chats list (every row's stamp on every render).
 */
function stampFormat(locale: string | undefined, style: keyof typeof STAMP_OPTIONS): Intl.DateTimeFormat {
	const key = `${locale ?? ''}|${style}`;
	let format = stampFormats.get(key);
	if (!format) {
		format = new Intl.DateTimeFormat(locale, STAMP_OPTIONS[style]);
		stampFormats.set(key, format);
	}
	return format;
}

export interface MailSummaryInput {
	id: string;
	subject: string;
	from_name: string | null;
	from_address: string | null;
	received_at: string | null;
	is_read: boolean;
}

export interface MailSummary<T extends MailSummaryInput = MailSummaryInput> {
	/** Unread messages, newest first. */
	unread: Array<T>;
	/** Epoch ms of the newest unread message, else the newest message, else null. */
	newestAt: number | null;
	/** Distinct sender display names among the unread, newest first. */
	senders: Array<string>;
}

function receivedMs(message: MailSummaryInput): number {
	const ms = message.received_at ? Date.parse(message.received_at) : Number.NaN;
	return Number.isFinite(ms) ? ms : 0;
}

export function mailSenderName(message: Pick<MailSummaryInput, 'from_name' | 'from_address'>): string {
	return message.from_name?.trim() || message.from_address?.trim() || '';
}

/** Reduce an inbox listing to what the Mail digest row needs. */
export function summariseMail<T extends MailSummaryInput>(messages: ReadonlyArray<T>): MailSummary<T> {
	const sorted = [...messages].sort((a, b) => receivedMs(b) - receivedMs(a));
	const unread = sorted.filter((message) => !message.is_read);
	const newest = unread[0] ?? sorted[0] ?? null;
	const newestAt = newest ? receivedMs(newest) || null : null;
	const senders: Array<string> = [];
	for (const message of unread) {
		const name = mailSenderName(message);
		if (name && !senders.includes(name)) senders.push(name);
	}
	return {unread, newestAt, senders};
}

export interface MailConversationInput extends MailSummaryInput {
	conversation_id?: string | null;
	is_draft?: boolean;
	is_pinned?: boolean;
}

export interface MailConversation<T extends MailConversationInput = MailConversationInput> {
	/** The newest mail in the conversation: its row shows this one. */
	message: T;
	/** How many mails in the conversation are unread. */
	unread: number;
}

/**
 * KONVO: mail as rows in Chats, one per conversation (JP 2026-10-04: not one Mail row for the
 * whole mailbox). Saved pin order first, then newest conversation first; drafts are not conversations.
 */
export function mailConversations<T extends MailConversationInput>(
	messages: ReadonlyArray<T>,
): Array<MailConversation<T>> {
	const pinOrder = new Map<string, number>();
	for (const message of messages) {
		if (message.is_draft || !message.is_pinned) continue;
		const key = message.conversation_id || message.id;
		if (!pinOrder.has(key)) pinOrder.set(key, pinOrder.size);
	}
	const sorted = [...messages].filter((message) => !message.is_draft).sort((a, b) => receivedMs(b) - receivedMs(a));
	const byConversation = new Map<string, MailConversation<T>>();
	for (const message of sorted) {
		const key = message.conversation_id || message.id;
		const existing = byConversation.get(key);
		if (existing) {
			if (!message.is_read) existing.unread += 1;
		} else {
			byConversation.set(key, {message, unread: message.is_read ? 0 : 1});
		}
	}
	return [...byConversation.values()].sort((a, b) => {
		const aRank = pinOrder.get(a.message.conversation_id || a.message.id) ?? Number.MAX_SAFE_INTEGER;
		const bRank = pinOrder.get(b.message.conversation_id || b.message.id) ?? Number.MAX_SAFE_INTEGER;
		return aRank - bRank;
	});
}
